/**
 * Each server's suggestions for its cameras' streams. They live on the media_server and come
 * through the relay.
 *
 * A `devices` change frame reloads the suggestions of the server it names: the server computes
 * them from its devices. The hook switches to the offline atom while the server is offline, and
 * the switch back loads the data again after a reconnect.
 */

import { atom, type WritableAtom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import type { SuggestionsSnapshot } from '@/types/devices';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import { SnapshotCommand, type SnapshotWrite } from '@/types/snapshot';

import { DEVICES_RELOAD_AFTER_MS } from './devicesAtoms';

const NO_SUGGESTIONS: SuggestionsSnapshot = { suggestions: null, isLoading: true, error: null };

/** One server's suggestions: read it for the snapshot, set it with a command. */
export const suggestionsAtom = atomFamily((serverId: string) => snapshotMemberAtom(
    snapshotAtoms<SuggestionsSnapshot>(
        NO_SUGGESTIONS,
        async (): Promise<SuggestionsSnapshot> => ({
            suggestions: await api.getSuggestions(serverId),
            isLoading: false,
            error: null,
        }),
        { reloadAfterMs: DEVICES_RELOAD_AFTER_MS },
    ),
));

const OFFLINE_SUGGESTIONS: SuggestionsSnapshot = {
    suggestions: null,
    isLoading: false,
    error: null,
};

/** What a reader gets while its server is offline: the relay cannot reach it, so none loads. */
export const offlineSuggestionsAtom: WritableAtom<
    SuggestionsSnapshot, [SnapshotWrite<SuggestionsSnapshot>], void
> = atom(
    (): SuggestionsSnapshot => OFFLINE_SUGGESTIONS,
    (_get, _set, _write: SnapshotWrite<SuggestionsSnapshot>): void => undefined,
);

const RELOAD = { command: SnapshotCommand.Reload } as const;

/** What the notifications stream means for the suggestions. */
export const suggestionsStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    // The gap may have hidden a change frame.
    if (input.kind === StreamInputKind.Reopened) {
        for (const serverId of suggestionsAtom.getParams()) {
            set(suggestionsAtom(serverId), RELOAD);
        }
        return;
    }
    if (input.kind !== StreamInputKind.Frame) {
        return;
    }
    const { frame } = input;
    if (frame.type !== 'change' || frame.resource !== 'devices') {
        return;
    }
    const { serverId } = frame;
    // Only an open tab has a member.
    if ([...suggestionsAtom.getParams()].includes(serverId)) {
        set(suggestionsAtom(serverId), RELOAD);
    }
});
